import { FC, useCallback } from "react";
import { useBoolean } from "usehooks-ts";

import { ContentSidebarHeader } from "../sidebar/ContentSidebarHeader";
import { ContentSidebarMain } from "../sidebar/ContentSidebarMain";

import { TopicSidebarPanelHeader } from "./TopicSidebarPanelHeader";
import { AdditionalResourceList } from "./activity/AdditionalResourceList";
import { ArchiveActivitiesList } from "./activity/ArchiveActivityList";
import { LessonActivityList } from "./activity/LessonActivityList";
import { PrepWorkActivityCard } from "./activity/PrepWorkActivityCard";
import { TopicDevTools } from "./activity/TopicDevTools";

import { DEVTOOL_QUERY_PARAM } from "components/constants";
import { ActivityCategory } from "components/server-types";
import { t } from "i18n/i18n";
import { ConfirmDeletionDialog } from "mds/components/ConfirmDeletionDialog";
import { HorizontalDivider } from "mds/components/HorizontalDivider";
import { useAppSelector } from "store/index";
import {
  selectIsEditModeEnabled,
  selectPagesForTopic,
  selectSortedTopicActivitiesByTopicId,
  selectTopicById,
} from "store/selectors";
import { isAdditionalResource, isLesson, isPrep } from "utils/activity";
import { BUTTON_EVENT_CATEGORY, trackEvent } from "utils/amplitude";
import { useNavigate, useQueryParamsManager } from "utils/routing";
import { useTopicActions } from "utils/topic";
import { courseHomeUrl } from "utils/urls";

export type TopicSidebarPanelProps = {
  topicId: string;
  scrollTrackingId?: string;
};

// This panel displays each type of activity and its child pages for a given topic.
// It is also for selecting the individual page/worksheet to be displayed in main page.
export const TopicSidebarPanel: FC<TopicSidebarPanelProps> = ({ topicId, scrollTrackingId }) => {
  const { getQueryParam } = useQueryParamsManager();
  const navigate = useNavigate();

  const topic = useAppSelector((s) => selectTopicById(s, topicId));
  const topicPages = useAppSelector((s) => selectPagesForTopic(s, topicId));
  const topicHasPages = topicPages.length > 0;
  const isEditModeEnabled = useAppSelector(selectIsEditModeEnabled);

  const activities = useAppSelector((s) => selectSortedTopicActivitiesByTopicId(s, topicId));

  const {
    value: confirmationDialogIsOpen,
    setTrue: openConfirmationDialog,
    setFalse: closeConfirmationDialog,
  } = useBoolean(false);

  // There should always be one prep category activity per topic.
  const prepWorkActivity = activities.find((activity) => isPrep(activity.category));

  const additionalResourceActivity = activities.find((activity) =>
    isAdditionalResource(activity.category),
  );

  const hasLessonPages = activities.some(
    (activity) => isLesson(activity.category) && activity.pages.length > 0,
  );

  const archiveHasPages = activities.some(
    (activity) => activity.category === ActivityCategory.ARCHIVE && activity.pages.length > 0,
  );

  const showDeveloperTools = getQueryParam(DEVTOOL_QUERY_PARAM) === "1";

  const { deleteTopic } = useTopicActions();

  const deleteTopicAndRedirect = useCallback(async () => {
    trackEvent("Topic - Delete", {
      ...BUTTON_EVENT_CATEGORY,
      context: "sidebar",
    });
    await deleteTopic(topic);
    navigate(courseHomeUrl(topic.course_id));
  }, [deleteTopic, navigate, topic]);

  const onDelete = useCallback(() => {
    if (topicHasPages) {
      openConfirmationDialog();
    } else {
      deleteTopicAndRedirect();
    }
  }, [deleteTopicAndRedirect, openConfirmationDialog, topicHasPages]);

  if (!topic)
    return (
      <>
        <ContentSidebarHeader visibleOnMobile>
          <h3>{t("glossary.topics")}</h3>
          <HorizontalDivider className="my-3 -ml-3" />
        </ContentSidebarHeader>
        <ContentSidebarMain className="pl-2 text-sm text-black-tint-40">
          {t("topic_sidebar.no_topics_available")}
        </ContentSidebarMain>
      </>
    );

  return (
    <>
      <ContentSidebarHeader unIndented visibleOnMobile>
        <TopicSidebarPanelHeader topic={topic} onDelete={onDelete} />
        <HorizontalDivider className="-ml-2 mt-2" />
      </ContentSidebarHeader>

      {topicHasPages || isEditModeEnabled ? (
        <ContentSidebarMain className="pt-2" scrollTrackingId={scrollTrackingId}>
          {prepWorkActivity && (isEditModeEnabled || prepWorkActivity?.pages?.length > 0) && (
            <>
              <PrepWorkActivityCard activities={activities} prepWorkActivity={prepWorkActivity} />
              <HorizontalDivider className="mb-2 mt-5" hideIfLast />
            </>
          )}

          {(hasLessonPages || isEditModeEnabled) && (
            <>
              <LessonActivityList activities={activities} topicId={topicId} />
              <HorizontalDivider className="mb-2 mt-5" hideIfLast />
            </>
          )}

          {additionalResourceActivity &&
            (isEditModeEnabled || additionalResourceActivity?.pages?.length > 0) && (
              <>
                <AdditionalResourceList
                  activities={activities}
                  additionalResourceActivity={additionalResourceActivity}
                />
                <HorizontalDivider className="mb-2 mt-5" hideIfLast />
              </>
            )}

          {(archiveHasPages || isEditModeEnabled) && (
            <ArchiveActivitiesList activities={activities} topicId={topicId} />
          )}

          {showDeveloperTools && <TopicDevTools activities={activities} />}
        </ContentSidebarMain>
      ) : (
        <ContentSidebarMain className="ml-2 mt-2.5" scrollTrackingId={scrollTrackingId}>
          <div className="body-s text-black-tint-40">{t("topic_sidebar.no_pages")}</div>
        </ContentSidebarMain>
      )}

      <ConfirmDeletionDialog
        glossaryKey="topic"
        instanceName={topic.title}
        open={confirmationDialogIsOpen}
        onClose={closeConfirmationDialog}
        onDelete={deleteTopicAndRedirect}
      />
    </>
  );
};
